/**
 * Device Fingerprinting Utility
 * Generates a unique fingerprint for the device/browser
 * Used for security features like device binding and new device detection
 */

/**
 * Generate a hash from a string
 */
const hashString = async (str) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
};

/**
 * Get WebGL fingerprint
 */
const getWebGLFingerprint = () => {
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl") || canvas.getContext("experimental-webgl");

    if (!gl) return "webgl-not-supported";

    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
    const vendor = debugInfo
      ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
      : "unknown";
    const renderer = debugInfo
      ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
      : "unknown";

    return `${vendor}~${renderer}`;
  } catch {
    return "webgl-error";
  }
};

/**
 * Get timezone info
 */
const getTimezoneInfo = () => {
  try {
    const offset = new Date().getTimezoneOffset();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return `${offset}~${timezone}`;
  } catch {
    return "timezone-error";
  }
};

/**
 * Get screen info
 */
const getScreenInfo = () => {
  try {
    const { width, height, colorDepth, pixelDepth } = window.screen;
    const devicePixelRatio = window.devicePixelRatio || 1;
    return `${width}x${height}x${colorDepth}x${pixelDepth}@${devicePixelRatio}`;
  } catch {
    return "screen-error";
  }
};

/**
 * Generate device fingerprint
 * Returns a stable hash that identifies this device/browser combination
 *
 * NOTE: We focus on STABLE characteristics that don't change between page loads.
 * Canvas fingerprint is excluded as it can vary slightly between loads.
 */
export const generateDeviceFingerprint = async () => {
  const components = [
    // Browser info (stable)
    navigator.userAgent,
    navigator.language,
    navigator.languages?.join(",") || "",
    navigator.platform,
    navigator.hardwareConcurrency || "unknown",
    navigator.maxTouchPoints || 0,

    // Screen (stable unless display changes)
    getScreenInfo(),

    // Timezone (stable unless user changes timezone)
    getTimezoneInfo(),

    // WebGL (stable - hardware-based)
    getWebGLFingerprint(),

    // Storage availability (stable)
    typeof localStorage !== "undefined" ? "ls" : "",
    typeof sessionStorage !== "undefined" ? "ss" : "",
    typeof indexedDB !== "undefined" ? "idb" : "",

    // Other stable properties
    navigator.cookieEnabled ? "cookies" : "",

    // NOTE: We intentionally EXCLUDE these unstable components:
    // - Canvas fingerprint (can vary between page loads)
    // - Plugins (can change)
    // - Fonts (expensive and can vary)
    // - doNotTrack (can change)
  ];

  const fingerprintString = components.join("|||");
  const hash = await hashString(fingerprintString);

  return hash;
};

/**
 * Get device info for logging/display
 */
export const getDeviceInfo = () => {
  const ua = navigator.userAgent;

  // Parse browser
  let browser = "Unknown";
  if (ua.includes("Firefox")) browser = "Firefox";
  else if (ua.includes("Chrome") && !ua.includes("Edg")) browser = "Chrome";
  else if (ua.includes("Safari") && !ua.includes("Chrome")) browser = "Safari";
  else if (ua.includes("Edg")) browser = "Edge";
  else if (ua.includes("Opera") || ua.includes("OPR")) browser = "Opera";

  // Parse OS
  let os = "Unknown";
  if (ua.includes("Windows")) os = "Windows";
  else if (ua.includes("Mac OS")) os = "macOS";
  else if (ua.includes("Linux")) os = "Linux";
  else if (ua.includes("Android")) os = "Android";
  else if (ua.includes("iOS") || ua.includes("iPhone") || ua.includes("iPad")) {
    os = "iOS";
  }

  // Device type
  let deviceType = "Desktop";
  if (/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) {
    deviceType = /iPad/i.test(ua) ? "Tablet" : "Mobile";
  }

  return {
    browser,
    os,
    deviceType,
    userAgent: ua,
    language: navigator.language,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

/**
 * Store fingerprint in localStorage for consistency across sessions
 * Falls back to sessionStorage if localStorage unavailable
 * Uses memory cache for performance within a page session
 */
let cachedFingerprint = null;
const STORAGE_KEY = "eScribe-device-fp";
const FINGERPRINT_VERSION = "v2"; // Increment to force regeneration if algorithm changes

/**
 * Get storage (prefer localStorage for persistence, fall back to sessionStorage)
 */
const getStorage = () => {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("__test__", "1");
      localStorage.removeItem("__test__");
      return localStorage;
    }
  } catch {
    // localStorage not available
  }
  try {
    if (typeof sessionStorage !== "undefined") {
      return sessionStorage;
    }
  } catch {
    // sessionStorage not available
  }
  return null;
};

export const getDeviceFingerprint = async () => {
  // Return from memory cache if available
  if (cachedFingerprint) {
    return cachedFingerprint;
  }

  const storage = getStorage();

  // Check storage for existing fingerprint
  if (storage) {
    const stored = storage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Check version matches
        if (parsed.version === FINGERPRINT_VERSION && parsed.fingerprint) {
          cachedFingerprint = parsed.fingerprint;
          return cachedFingerprint;
        }
      } catch {
        // Invalid stored data, regenerate
      }
    }
  }

  // Generate new fingerprint
  const fingerprint = await generateDeviceFingerprint();
  cachedFingerprint = fingerprint;

  // Store with version for future validation
  if (storage) {
    try {
      storage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          version: FINGERPRINT_VERSION,
          fingerprint,
          createdAt: new Date().toISOString(),
        }),
      );
    } catch {
      // Storage write failed, fingerprint still works for this session
    }
  }

  return fingerprint;
};

export default {
  generateDeviceFingerprint,
  getDeviceFingerprint,
  getDeviceInfo,
};
